import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Phone, ClipboardList } from "lucide-react";
import { BRAND, TAX_SETTINGS, fmt } from "../data/restaurant";
import { useCart } from "../context/CartContext";
import OrderSummaryModal from "./OrderSummaryModal";

export default function CartDrawer() {
  const { items, isOpen, setOpen, inc, dec, remove, clear, count, subtotal } = useCart();
  const [summaryOpen, setSummaryOpen] = useState(false);

  const tax =
    TAX_SETTINGS.configured && TAX_SETTINGS.ratePercent != null
      ? (subtotal * TAX_SETTINGS.ratePercent) / 100
      : null;
  const total = subtotal + (tax ?? 0);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, setOpen]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[115] bg-jet/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              data-testid="cart-overlay"
            />
            <motion.aside
              role="dialog"
              aria-modal="true"
              aria-label={`Your cart, ${count} items`}
              data-testid="cart-drawer"
              initial={{ x: "105%" }}
              animate={{ x: 0 }}
              exit={{ x: "105%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              className="fixed right-0 top-0 z-[120] flex h-full w-full max-w-md flex-col border-l-4 border-jet bg-cream"
            >
              <div className="flex items-center justify-between border-b-4 border-jet bg-amber px-5 py-4">
                <h2 className="flex items-center gap-2 font-display text-xl font-black uppercase text-jet" data-testid="cart-drawer-title">
                  <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                  Your Cart ({count})
                </h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="cart-close-button"
                  aria-label="Close cart"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-jet bg-cream shadow-hard-sm transition-transform hover:rotate-90"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {items.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty-state">
                  <ShoppingBag className="h-14 w-14 text-jet/25" aria-hidden="true" />
                  <p className="font-display text-xl font-black uppercase text-jet">Your cart is empty</p>
                  <p className="text-sm font-semibold text-jet/60">Add something tasty from the menu.</p>
                  <a
                    href="#menu"
                    onClick={() => setOpen(false)}
                    data-testid="cart-empty-browse-button"
                    className="mt-2 inline-flex items-center gap-2 rounded-full border-[3px] border-jet bg-jet px-6 py-3 font-display text-sm font-extrabold uppercase text-cream shadow-hard-flame transition-all hover:-translate-y-0.5"
                  >
                    Browse the Menu
                  </a>
                </div>
              ) : (
                <>
                  <ul className="flex-1 divide-y-2 divide-dotted divide-jet/20 overflow-y-auto px-5" data-testid="cart-items-list">
                    {items.map((i) => (
                      <li key={i.id} className="flex gap-3 py-4" data-testid="cart-item-row">
                        {i.img && (
                          <img
                            src={i.img}
                            alt={i.name}
                            className="h-16 w-16 shrink-0 rounded-xl border-2 border-jet object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate font-display text-base font-extrabold text-jet">{i.name}</p>
                              <p className="text-xs font-bold text-jet/60">
                                {fmt(i.price)} CAD each
                                {i.options && i.options.length > 0 ? ` · ${i.options.join(", ")}` : ""}
                              </p>
                            </div>
                            <span className="shrink-0 font-display text-base font-black text-crimson" data-testid="cart-line-total">
                              {fmt(i.price * i.qty)}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="inline-flex items-center overflow-hidden rounded-full border-[3px] border-jet bg-cream">
                              <button
                                type="button"
                                onClick={() => dec(i.id)}
                                data-testid={`cart-decrease-${i.id}`}
                                aria-label={`Decrease ${i.name} quantity`}
                                className="inline-flex h-8 w-8 items-center justify-center transition-colors hover:bg-sunshine/60"
                              >
                                <Minus className="h-3.5 w-3.5" aria-hidden="true" />
                              </button>
                              <span className="w-8 text-center font-display text-sm font-black" data-testid={`cart-qty-${i.id}`}>
                                {i.qty}
                              </span>
                              <button
                                type="button"
                                onClick={() => inc(i.id)}
                                data-testid={`cart-increase-${i.id}`}
                                aria-label={`Increase ${i.name} quantity`}
                                className="inline-flex h-8 w-8 items-center justify-center transition-colors hover:bg-sunshine/60"
                              >
                                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => remove(i.id)}
                              data-testid={`cart-remove-${i.id}`}
                              aria-label={`Remove ${i.name} from cart`}
                              className="inline-flex items-center gap-1 rounded-full border-2 border-jet/20 px-3 py-1 text-xs font-bold uppercase tracking-wide text-jet/60 transition-colors hover:border-crimson hover:text-crimson"
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                              Remove
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="border-t-4 border-jet bg-cream px-5 py-4">
                    <div className="rounded-2xl border-[3px] border-jet bg-sunshine/40 px-4 py-3 text-sm font-bold text-jet" data-testid="cart-totals">
                      <div className="flex items-center justify-between">
                        <span>Subtotal</span>
                        <span className="font-display" data-testid="cart-subtotal">{fmt(subtotal)} CAD</span>
                      </div>
                      {TAX_SETTINGS.configured ? (
                        <>
                          <div className="mt-1 flex items-center justify-between">
                            <span>GST ({TAX_SETTINGS.gstPercent}%)</span>
                            <span className="font-display" data-testid="cart-tax-gst">{fmt((subtotal * TAX_SETTINGS.gstPercent) / 100)} CAD</span>
                          </div>
                          <div className="mt-1 flex items-center justify-between">
                            <span>PST ({TAX_SETTINGS.pstPercent}%)</span>
                            <span className="font-display" data-testid="cart-tax-pst">{fmt((subtotal * TAX_SETTINGS.pstPercent) / 100)} CAD</span>
                          </div>
                        </>
                      ) : (
                        <div className="mt-1 flex items-center justify-between">
                          <span>Taxes ({TAX_SETTINGS.label})</span>
                          <span className="font-display" data-testid="cart-tax">{tax != null ? `${fmt(tax)} CAD` : "At confirmation"}</span>
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between border-t-2 border-jet/30 pt-2">
                        <span className="font-display text-base font-black uppercase">Estimated Total</span>
                        <span className="font-display text-lg font-black text-crimson" data-testid="cart-estimated-total">{fmt(total)} CAD</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setSummaryOpen(true)}
                      data-testid="cart-call-order-button"
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-crimson px-6 py-4 font-display text-base font-extrabold uppercase text-cream shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard"
                    >
                      <Phone className="h-5 w-5" aria-hidden="true" />
                      Call to Place Pickup Order
                    </button>

                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={clear}
                        data-testid="cart-clear-button"
                        className="text-xs font-bold uppercase tracking-wide text-jet/50 transition-colors hover:text-crimson"
                      >
                        Clear cart
                      </button>
                      <span className="text-xs font-semibold text-jet/40">All prices in CAD · pay at pickup</span>
                    </div>
                  </div>
                </>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <OrderSummaryModal open={summaryOpen} onClose={() => setSummaryOpen(false)} />
    </>
  );
}
